import React, { useState } from 'react'
import { FilePond } from 'react-filepond'
import axios from 'axios'
import moment from 'moment/moment'
import { useQuery } from 'react-query'

import { ReactComponent as Spinner } from '../../images/icons/spinner.svg'
import classNames from 'classnames'
import { toast } from 'react-toastify'

const s3 = axios.create({})

const ImportComponents = () => {
  const [inProgress, setProgress] = useState(false)
  const [files, setFiles] = useState([])

  const {
    data: componentFile,
    isLoading: isLoadingComponentFile,
    refetch: refetchComponentFile,
  } = useQuery('/api/migration/component',
    async () => {
      const { data } = await axios.get('/api/migration/component')
      return data
    }, {
      retry: false,
    })

  const {
    data: CSVFile,
    refetch: refetchCSVFile,
  } = useQuery('/api/migration/component_csv',
    async () => {
      const { data } = await axios.get('/api/migration/component_csv')
      return data
    }, {
      retry: false,
    })

  const process = async (
    fieldName, file, metadata, load, error, progress, abort) => {
    try {
      const { data: url } = await axios.get(
        '/api/migration/presigned_upload_component?fileName=' + file.name)
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()

      await s3.put(url, file, {
        cancelToken: source.token,
        onUploadProgress: (e) => {
          progress(e.lengthComputable, e.loaded, e.total)
        },
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        transformRequest: [
          (data, headers) => {
            if (headers.setAuthorization) {
              headers.setAuthorization(undefined)
            } else if (headers.common && headers.common.Authorization) {
              delete headers.common.Authorization
            }
            return data
          }],
      })
      load('Uploaded')
      const res = await axios.put(
        '/api/migration/component?fileName=' + file.name)
      if (typeof res.data === 'string' && res.data.startsWith('ERROR:')) {
        // an error occurred when converting the uploaded file to Component CSV...
        // it could not be processed (and the component CSV likely does not exist)
        // display to user (todo: where?)
        toast.error(
          <div>
            {res.data}
          </div>, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 5000,
          })
      }

      setFiles([file])
      refetchComponentFile()
      refetchCSVFile()
    } catch (error) {
      if (error.response) {
        console.log(error.response.data)
        console.log(error.response.status)
        console.log(error.response.headers)
      } else if (error.request) {
        console.log(error.request)
      } else {
        console.log('Error', error.message)
      }
      console.log(error.config)
    }
  }
  const onRemoveComponentFile = async (error, file) => {
    await axios.delete('/api/migration/component')
    await refetchComponentFile()
  }

  const uploadCSV = async (event) => {
    try {
      setProgress(true)
      const file = event.target.files[0]
      const { data: url } = await axios.get(
        '/api/migration/presigned_upload_csv?fileName=Components.csv')

      await s3.put(url, file, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        transformRequest: [
          (data, headers) => {
            if (headers.setAuthorization) {
              headers.setAuthorization(undefined)
            } else if (headers.common && headers.common.Authorization) {
              delete headers.common.Authorization
            }
            return data
          }],
      })

      await refetchCSVFile()
      const res = await axios.put('/api/migration/component_csv')
      if (typeof res.data === 'string' && res.data.startsWith('ERROR:')) {
        // an error occurred when converting the uploaded Component CSV to Elements JSON...
        // the CSV has some problem and needs to be modified before it can be processed.
        // display to user (todo: where?)
        toast.error(
          <div>
            {res.data}
          </div>, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 5000,
          })
      }
    } catch (error) {
      if (error.response) {
        console.log(error.response.data)
        console.log(error.response.status)
        console.log(error.response.headers)
      } else if (error.request) {
        console.log(error.request)
      } else {
        console.log('Error', error.message)
      }
      console.log(error.config)
    } finally {
      setProgress(false)
    }
  }

  const download = async () => {
    try {
      const { data: url } = await axios.get(
        '/api/migration/presigned_download_csv?fileName=' + CSVFile.Name)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', CSVFile.Name)
      document.body.appendChild(link)
      link.click()
    } catch (error) {
      if (error.response) {
        console.log(error.response.data)
        console.log(error.response.status)
        console.log(error.response.headers)
      } else if (error.request) {
        console.log(error.request)
      } else {
        console.log('Error', error.message)
      }
      console.log(error.config)
    }
  }
  if (isLoadingComponentFile) {
    return (<div className={'row'}>
      <div className={'col-3'}>Loading...</div>
    </div>)
  }

  return (
    <div className={'row'}>
      <div className={'col-3'}>
        <div>
          <label htmlFor={'uploadComponentFile'} className={'d-inline'}>Upload
            HotDocs Component File (.cmp)
          </label>
          <FilePond
            id={'uploadComponentFile'}
            name={'ComponentFile'}
            className={'mb-0'}
            files={files.length ? files : componentFile ? [
              {
                source: 'client',
                options: {
                  type: 'local',
                  file: {
                    name: componentFile.Name,
                    size: componentFile.Size,
                    type: 'text',
                  },
                },
              }] : []}
            onremovefile={onRemoveComponentFile}
            server={{
              process,
            }}
            credits={{ label: '', url: '' }}
          />
          {componentFile &&
            <small>Component file uploaded: {moment(componentFile.LastModified).
              format('L LTS')}</small>}
          <button
            className={'btn btn-secondary btn-block p-1 btn-theme mt-2 mb-0'}
            onClick={download}
            disabled={!CSVFile || inProgress}
          >Download
            Component CSV
          </button>

          <label
            htmlFor={'updatedComponentCSV'}
            className={classNames(
              'btn btn-secondary btn-block p-1 btn-theme mt-4',
              { 'disabled': !CSVFile || inProgress })}
          >{inProgress ? <Spinner className={'rot'} style={{
            width: 30,
            top: -4,
            position: 'relative',
          }}/> : 'Upload Modified Component CSV'}
          </label>
          <input type="file"
                 className="d-none"
                 onChange={uploadCSV}
                 accept=".csv" id="updatedComponentCSV"/>
          {CSVFile &&
            <small>Component CSV last updated: {moment(CSVFile.LastModified).
              format('L LTS')}</small>}
        </div>
      </div>
      <div className={'col-9'}>Video</div>
    </div>
  )
}

export default ImportComponents
